import React, { useRef, useState, useEffect } from "react";
import AppAppBar from "./views/AppAppBar";
import PublishingHero from "./views/PublishingHero";
import AppFooter from "./views/AppFooter";
import Box from "@mui/material/Box";
import Grid from "@mui/material/GridLegacy";
import Typography from "./components/Typography";
import withRoot from "../../withRoot";

const Publishing: React.FC = () => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleSections, setVisibleSections] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.5 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppAppBar />
      <PublishingHero />

      {/* World-Class Publishing Section */}
      <Box
        sx={{
          maxWidth: { xs: "100%", md: "50vw" }, // Mobile: full width, Desktop: 50vw
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" }, // Horizontal padding on mobile to prevent overflow
          mt: { xs: 4, md: 8 }, // Consistent margin with other pages
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          WORLD-CLASS PUBLISHING
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          InkBowl originated as and continues to be is a selective international
          literature and arts publishing platform for students. We accept a
          broad range of visual art forms and non-academic literature. As a
          Creator in the InkBowl Network, you are a candidate to be formally
          published, become the recipient of unique credentials, and be
          recognized by a global audience. InkBowl offers opportunities to be
          featured in both digital and in-print magazine issues. Competitions
          are also hosted on occassion.
        </Typography>
      </Box>

      {/* Why Our Mission Matters Section */}
      <Box
        sx={{
          maxWidth: { xs: "100%", md: "50vw" }, // Mobile: full width, Desktop: 50vw
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" }, // Horizontal padding on mobile to prevent overflow
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          WHY OUR MISSION MATTERS FOR <em>YOU</em>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          InkBowl addresses an important disparity: students lack the
          opportunity to publish their work. This discrepancy is even greater
          for artists. Other credible publishers often rely exclusively on a
          competition-based model– if you don&apos;t place above your rivals,
          you do not receive the formal acknowledgement you deserve. Limited
          submission periods to enter are yet another barrier. InkBowl proudly
          allocates a multitude of awards and certificates, and applications are
          rolling.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          Our goal is to provide students with the means to go from a committed
          or casual hobbyist to an internationally published author or artist.
          This distinction matters– for your future career, resumé, or higher
          education applications.
        </Typography>
      </Box>

      {/* Digital Publication vs InkBowl Magazine Section */}
      <Box
        sx={{
          backgroundColor: "black",
          color: "white",
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          sx={{
            maxWidth: "1200px",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          {/* Digital Publication Column */}
          <Grid item xs={12} sm={6} md={5}>
            <Box
              sx={{
                textAlign: "left",
                px: { xs: 2, md: 4 },
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  mb: 2,
                  color: "white",
                }}
              >
                DIGITAL PUBLICATION
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  mb: 4,
                  color: "white",
                }}
              >
                year-round online
              </Typography>

              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Submission Basis:</strong> Rolling
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Format:</strong> Digital
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Publication Frequency:</strong> Continuous <br />{" "}
                  (upon acceptance)
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Contributor Volume:</strong> No limit
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Distribution:</strong> Available via our online
                  archive
                </Typography>
              </Box>

              <Box
                sx={{ mt: "auto", display: "flex", justifyContent: "center" }}
              >
                <Box
                  component="button"
                  sx={{
                    backgroundColor: "white",
                    color: "black",
                    border: "none",
                    px: 4,
                    py: 1.5,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  onClick={() => window.open("/dashboard")}
                >
                  APPLY NOW
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Divider */}
          <Grid
            item
            xs={12}
            sm={1}
            md={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: { xs: "80%", sm: "1px" },
                height: { xs: "1px", sm: "400px" },
                backgroundColor: "white",
                opacity: 0.4,
              }}
            />
          </Grid>

          {/* InkBowl Magazine Column */}
          <Grid item xs={12} sm={6} md={5}>
            <Box
              sx={{
                textAlign: "left",
                px: { xs: 2, md: 4 },
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  mb: 2,
                  color: "white",
                }}
              >
                INKBOWL MAGAZINE
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  mb: 4,
                  color: "white",
                }}
              >
                biannual in-print
              </Typography>

              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Submission Basis:</strong> Rolling
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Format:</strong> In-print and digital
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Publication Frequency:</strong> Biannual <br />{" "}
                  (Spring & Fall editions)
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Contributor Volume:</strong> Limited; varies between
                  issues
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "white",
                  }}
                >
                  <strong>• Distribution:</strong> Print issues can be ordered
                </Typography>
              </Box>

              <Box
                sx={{ mt: "auto", display: "flex", justifyContent: "center" }}
              >
                <Box
                  component="button"
                  sx={{
                    backgroundColor: "white",
                    color: "black",
                    border: "none",
                    px: 4,
                    py: 1.5,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    fontWeight: "bold",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  onClick={() => window.open("/dashboard")}
                >
                  APPLY NOW
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Accepted Formats Section */}
      <Box
        sx={{
          backgroundImage: "url(/images/publishing-bg.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: { xs: "60vh", md: "70vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "#2B2D6A",
            padding: { xs: "40px 30px", md: "60px 80px" },
            textAlign: "center",
            position: "relative",
            zIndex: 1,
            maxWidth: { xs: "90%", md: "800px" },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              mb: 3,
              color: "white",
            }}
          >
            ACCEPTED FORMATS
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1rem", md: "1.3rem" },
              mb: 4,
              color: "white",
              fontWeight: "normal",
              lineHeight: 1.4,
            }}
          >
            INKBOWL PUBLISHES 50 TYPES OF LITERATURE,
            <br />
            VISUAL ART, AND MULTIMEDIA
          </Typography>

          <Box
            component="button"
            onClick={() => {
              window.location.href = "/submissions";
            }}
            sx={{
              backgroundColor: "black",
              color: "white",
              border: "none",
              px: { xs: 3, md: 4 },
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: "0.9rem", md: "1rem" },
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            EXPLORE
          </Box>
        </Box>
      </Box>

      {/* Credentials & Awards Section */}
      <Box
        sx={{
          backgroundColor: "white",
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "1200px" }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 6,
              textAlign: "center",
              color: "black",
            }}
          >
            CREDENTIALS & AWARDS
          </Typography>

          <Grid
            container
            spacing={{ xs: 4, md: 8 }}
            sx={{ justifyContent: "center" }}
          >
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              {/* General Publication Credentials */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  GENERAL PUBLICATION CREDENTIALS
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  These are automatically awarded upon successful publication in
                  the respective format:
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Published Contributor – Digital Edition
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For all students published online.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Published Contributor – InkBowl Magazine
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For students selected for the more competitive
                    in-print/multimedia version.
                  </Typography>
                </Box>
              </Box>

              {/* Spotlight Recognition */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  SPOTLIGHT RECOGNITION (Biannual Magazine)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  For work deemed to be of the highest caliber and highlighted
                  in InkBowl&apos;s print issue:
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Featured Cover Artist
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to the student whose artwork is selected for the
                    front cover.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Spotlight Visual Artist
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For select visual artists featured prominently in the
                    edition.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    3. Spotlight Author
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For select writers featured prominently in the edition.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              {/* National Talent Honors */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  NATIONAL TALENT HONORS
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  Celebrating two gifted United States citizen students each
                  season (4x annually).
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Best in U.S. – Creative Writing
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to the most outstanding U.S.-based writing
                    submission.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Best in U.S. – Visual Art
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to the most outstanding U.S.-based artwork.
                  </Typography>
                </Box>
              </Box>

              {/* Editorial Distinction Awards */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  EDITORIAL DISTINCTION AWARDS (Editors&apos; Picks)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  For standout works selected by the editorial board, presented
                  on a rolling basis:
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Editors&apos; Pick – Independent Work
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Exceptional standalone art or writing pieces.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Editors&apos; Pick – Collections
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For several art or literature works submitted together as a
                    collection.
                  </Typography>
                </Box>
              </Box>

              {/* Divisions */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                  }}
                >
                  <strong>Divisions</strong> – I. Middle School, II. High
                  School, III. Undergraduate, IV. Graduate
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Second Row - International and Multicultural Honors */}
          <Grid
            container
            spacing={{ xs: 4, md: 8 }}
            sx={{ justifyContent: "center", mt: 4 }}
          >
            {/* Left Column - International Talent Honors */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  INTERNATIONAL TALENT HONORS
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  Appreciating the phenomenal work of students who live outside
                  the U.S.
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Best of International – Creative Writing
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to the most outstanding writing submission from
                    non-U.S. students.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Best of International – Visual Art
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to the most outstanding artwork from non-U.S.
                    students.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Multicultural Honors */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    mb: 1,
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  MULTICULTURAL HONORS
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: "#666",
                    textAlign: "left",
                  }}
                >
                  For students whose work transcends boarders— geographic,
                  cultural, or linguistic.
                </Typography>

                <Box sx={{ mb: 2, textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    1. Best Creative Writing by an ESL Author
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    Awarded to a student whose primary or first language is not
                    English.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      mb: 0.5,
                      ml: 2,
                    }}
                  >
                    2. Transnational Voices Award
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.8rem", md: "0.85rem" },
                      color: "#666",
                      ml: 4,
                    }}
                  >
                    For powerful work that expresses global, diasporic, or
                    cross-cultural experiences.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <AppFooter />
    </Box>
  );
};

export default withRoot(Publishing);
